import React from 'react';
import { marketplaceFeaturesData } from '../../data/marketplaceData';
import { Clock, Zap, BarChart3, FileText, ArrowRight } from 'lucide-react';

const iconMap = {
  Clock,
  Zap,
  BarChart3,
  FileText
};

const MarketplaceFeatures = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
            Características
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Simplifica las transacciones de{' '}
            <span className="text-[#1297fd]">tus clientes</span>
          </h2>
          <p className="text-lg text-[#c3c3c5] max-w-3xl mx-auto">
            Un marketplace white-label hace fácil para clientes y resellers comprar, gestionar y rastrear suscripciones con automatización e insights en tiempo real.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {marketplaceFeaturesData.map((feature) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div
                key={feature.id}
                className="group bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all duration-500 hover:shadow-2xl hover:shadow-[#1297fd]/10 hover:-translate-y-2"
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                  {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-[#c3c3c5] text-sm leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Dashboard Preview */}
        <div className="mt-20 relative">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-2xl overflow-hidden">
            {/* Browser Chrome */}
            <div className="flex items-center gap-2 mb-6">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 bg-white/10 rounded-lg py-2 px-4 ml-4">
                <span className="text-white/60 text-sm">marketplace.asisant.com</span>
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Sidebar */}
              <div className="bg-white/5 rounded-2xl p-4">
                <div className="space-y-3">
                  {['Dashboard', 'Catálogo', 'Suscripciones', 'Facturas', 'Configuración'].map((item, idx) => (
                    <div
                      key={item}
                      className={`px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                        idx === 0 ? 'bg-[#1297fd] text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Main Content */}
              <div className="md:col-span-2 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Ingresos', value: '$87.5K', change: '+18%' },
                    { label: 'Clientes', value: '1,247', change: '+12%' },
                    { label: 'Suscripciones', value: '3,892', change: '+24%' }
                  ].map((stat) => (
                    <div key={stat.label} className="bg-white/5 rounded-xl p-4">
                      <p className="text-[#8bcaf9] text-xs mb-1">{stat.label}</p>
                      <p className="text-white font-bold text-xl">{stat.value}</p>
                      <span className="text-green-400 text-xs">{stat.change}</span>
                    </div>
                  ))}
                </div>

                {/* Vendor List */}
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-white font-medium">Vendors Activos</span>
                    <span className="text-[#40aaf8] text-sm cursor-pointer hover:underline">Ver todos</span>
                  </div>
                  <div className="space-y-2">
                    {['Microsoft 365', 'Adobe Creative', 'AWS Services', 'Google Workspace'].map((vendor) => (
                      <div key={vendor} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                        <span className="text-white/80 text-sm">{vendor}</span>
                        <span className="text-green-400 text-xs">Activo</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#8bcaf9]/20 rounded-full blur-2xl" />
          <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#1297fd]/20 rounded-full blur-2xl" />
        </div>
      </div>
    </section>
  );
};

export default MarketplaceFeatures;
